import { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import type { ElementType, MouseEvent } from 'react';

import Button from '../Button';
import Grid from '../Grid';
import Icon from '../Icon';
import Image from '../Image';
import Link from '../Link';
import Tooltip from '../Tooltip';
import VisuallyHiddenText from '../VisuallyHiddenText';
import { useI18n, useElement } from '../../hooks';
import { isValidElement } from '../../utils';

import NavigationList from './NavigationList';
import AppShellContext, { AppShellDrawerContext } from './AppShellContext';
import AppShellDrawer from './Drawer';
import { AppShellSearch } from './AppShellSearch';
import {
  StyledAppInfo,
  StyledCaseTypes,
  StyledScrollWrap,
  StyledUtils,
  StyledNavCasesList,
  StyledCountIcon,
  StyledUtilIconCount,
  StyledAppShellTooltip,
  StyledUtilsSeparator,
  StyledAIAssistant,
  headerHeight
} from './AppShell.styles';
import type {
  AgentProps,
  AppInfoProps,
  CaseProps,
  CaseTypeProps,
  LinkProps,
  NavListItemProps,
  UtilItemProps,
  UtilsProps
} from './AppShell.types';
import type { AppNavigationPanelProps } from './AppNavigationPanel.types';

export const CaseTypes = ({
  caseTypes,
  mobileNavBar
}: {
  caseTypes: CaseTypeProps[];
  mobileNavBar?: boolean;
}) => {
  const t = useI18n();

  const transformedCaseTypes = caseTypes.map(caseType => {
    return {
      ...caseType,
      visual: caseType.icon ? <Icon name={caseType.icon} /> : undefined,
      primary: caseType.name
    };
  });

  return useMemo(
    () => (
      <StyledCaseTypes>
        <NavigationList
          items={[
            {
              primary: t('app_shell_create'),
              visual: <Icon name='plus' />,
              items: transformedCaseTypes
            }
          ]}
          mobileNavBar={mobileNavBar}
        />
      </StyledCaseTypes>
    ),
    [caseTypes, mobileNavBar]
  );
};

export const Links = (props: { links: LinkProps[]; mobileNavBar?: boolean }) => {
  const { links, mobileNavBar } = props;
  const [transformedLinks, setTransformedLinks] = useState<NavListItemProps[]>([]);

  // TODO: Can this be a memo instead of a state and an effect?
  const mergedNavLinks = (items: LinkProps[]): NavListItemProps[] => {
    return items.map((link: LinkProps) => {
      return {
        ...link,
        primary: link.name,
        'aria-current': link.active ? 'page' : null,
        visual: link.icon ? <Icon name={link.icon} /> : null,
        onClick: link.links ? undefined : link.onClick,
        items: link.links ? mergedNavLinks(link.links) : undefined
      };
    });
  };

  useEffect(() => {
    setTransformedLinks(mergedNavLinks(links));
  }, [links]);

  if (!links.length) return null;

  return (
    <div>
      <NavigationList items={transformedLinks} mobileNavBar={mobileNavBar} />
    </div>
  );
};

const AIAssistant = ({ agent }: { agent: AgentProps }) => (
  <StyledAIAssistant>
    <NavigationList
      items={[
        {
          id: agent.id,
          primary: agent.name,
          visual: agent.icon,
          'aria-current': agent.active ? 'page' : null,
          onClick: agent.onClick
        }
      ]}
    />
  </StyledAIAssistant>
);

const Cases = (props: { cases: CaseProps[] }) => {
  const { cases } = props;

  const setupCases = (items: CaseProps[]): NavListItemProps[] => {
    return items.map((item: CaseProps) => {
      const {
        id,
        primary,
        secondary,
        collapsedItemInfo,
        visual = <Icon name='case-solid' />,
        active,
        onClick,
        onDismiss,
        ...restProps
      } = item;
      return {
        ...restProps,
        id,
        'aria-current': active ? 'page' : undefined,
        primary,
        secondary,
        collapsedItemInfo,
        active: !!active,
        onClick,
        onDismiss,
        visual
      };
    });
  };

  return (
    <StyledNavCasesList>
      <NavigationList items={setupCases(cases)} />
    </StyledNavCasesList>
  );
};

export const Utils = ({ items }: UtilsProps) => {
  const { navOpen, navState, drawerOpen, setDrawerOpen, appHeaderEl } = useContext(AppShellContext);
  const [item, setItem] = useState<UtilItemProps>();

  useEffect(() => {
    if (drawerOpen && item) {
      setItem(
        items?.find<UtilItemProps>(
          (utilItem): utilItem is UtilItemProps =>
            !isValidElement(utilItem) && utilItem.name === item.name
        )
      );
    }
  }, [items]);

  const transform = useCallback(
    (utilItem: UtilItemProps): NavListItemProps => {
      const uItem = { ...utilItem };
      delete uItem.onDrawerClose;
      delete uItem.onDrawerOpen;
      return {
        onClick: () => {
          if (utilItem.drawerView) {
            setItem(utilItem);
          }
        },
        ...uItem,
        primary: utilItem.name,
        visual: utilItem.count ? (
          <StyledUtilIconCount>
            {utilItem.visual}
            <StyledCountIcon variant='urgent' id={`${utilItem.name}-count`}>
              {utilItem.count}
            </StyledCountIcon>
          </StyledUtilIconCount>
        ) : (
          utilItem.visual
        ),
        collapseItems: !navOpen
      };
    },
    [navOpen]
  );

  const utilItems = useMemo(() => {
    return [
      ...(items ?? []).map(uItem => {
        return isValidElement(uItem) ? uItem : transform(uItem);
      })
    ];
  }, [items, transform]);

  useEffect(() => {
    if (item) setDrawerOpen(true);
  }, [item]);

  useEffect(() => {
    if (navState === 'closed' || navState === 'closing') setDrawerOpen(false);
  }, [navState]);

  // setDrawerOpen comes from AppShellContext (raw useState setter — stable reference).
  // setItem is the local useState setter — also stable. Neither needs to be in deps.
  // openDrawer and refreshDrawer have identical bodies: both spread `next` to create
  // a new object reference so useEffect([item]) always fires, even when the same item
  // is re-opened. The semantic distinction is: openDrawer starts a new drawer session
  // (useEffect triggers setDrawerOpen(true)); refreshDrawer replaces an already-open
  // item's content (setDrawerOpen(true) is a no-op when already open).
  const ctxValue = useMemo(
    () => ({
      drawerOpen,
      openItemName: item?.name,
      closeDrawer: () => {
        setDrawerOpen(false);
      },
      openDrawer: (next: UtilItemProps) => {
        setItem({ ...next });
      },
      refreshDrawer: (next: UtilItemProps) => {
        setItem({ ...next });
      }
    }),
    [drawerOpen, item?.name]
  );

  return (
    <AppShellDrawerContext.Provider value={ctxValue}>
      <StyledUtils>
        <NavigationList items={utilItems} collapsed />
      </StyledUtils>

      {item && (
        <AppShellDrawer
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          content={item.drawerView}
          header={item.drawerHeader || item.name}
          topOffset={appHeaderEl ? headerHeight : '0'}
          onDrawerClose={() => {
            setItem(undefined);
            item.onDrawerClose?.();
          }}
          onDrawerOpen={item.onDrawerOpen}
        />
      )}
    </AppShellDrawerContext.Provider>
  );
};

const AppInfo = ({ appInfo }: { appInfo: AppInfoProps }) => {
  const [appInfoEl, setAppInfoEl] = useElement<HTMLAnchorElement>();
  const { navState } = useContext(AppShellContext);

  const {
    appName,
    portalName,
    href: appInfoHref,
    onClick: appInfoOnClick,
    imageSrc: logoSrc,
    appNameHidden,
    ...appInfoRestProps
  } = appInfo;

  let appInfoAs: ElementType = 'div';
  if (appInfoHref) appInfoAs = Link;
  else if (appInfoOnClick) appInfoAs = Button;

  return (
    <>
      <StyledAppInfo
        as={Grid}
        forwardedAs={appInfoAs}
        variant='link'
        href={appInfoHref}
        onClick={appInfoOnClick}
        aria-label={`${appName}${portalName ? ` - ${portalName}` : ''}`}
        {...appInfoRestProps}
        container={{
          alignItems: 'center',
          areas: portalName ? '"logo app" "logo portal"' : '"logo app"'
        }}
        ref={setAppInfoEl}
        icon
      >
        <Grid item={{ area: 'logo' }} as={Image} src={logoSrc} alt={appName} />
        <Grid item={{ area: 'app' }} as={appNameHidden ? VisuallyHiddenText : 'span'}>
          {appName}
        </Grid>
        {portalName && (
          <Grid item={{ area: 'portal' }} as='span'>
            {portalName}
          </Grid>
        )}
      </StyledAppInfo>
      {navState === 'closed' && (!appNameHidden || portalName) && (
        <Tooltip
          target={appInfoEl}
          placement='right'
          as={StyledAppShellTooltip}
          showDelay='none'
          hideDelay='none'
        >
          {`${appNameHidden ? '' : appName} ${portalName ?? ''}`.trim()}
        </Tooltip>
      )}
    </>
  );
};

const AppNavigationPanel = ({
  appInfo,
  navState,
  searchInput,
  searchLabel,
  onClick,
  caseTypes,
  links,
  cases,
  utils,
  agent
}: AppNavigationPanelProps) => {
  return (
    <>
      {appInfo && <AppInfo appInfo={appInfo} />}
      {searchInput && (
        <AppShellSearch
          searchLabel={searchLabel}
          collapsed={['closed', 'closing'].includes(navState)}
          searchInput={searchInput}
        />
      )}
      <StyledScrollWrap
        container={{ direction: 'column' }}
        item={{ grow: 1 }}
        tabIndex={-1}
        onClick={(e: MouseEvent<HTMLElement>) => {
          // Ensure that clicks on the container (not on interactive child elements) trigger the onClick handler
          if (e.target === e.currentTarget) onClick?.();
        }}
      >
        {caseTypes && caseTypes.length > 0 && <CaseTypes caseTypes={caseTypes} />}
        {links && <Links links={links} />}
        {cases && <Cases cases={cases} />}
        {['open', 'opening'].includes(navState) && (
          <StyledUtilsSeparator navOpen={['open', 'opening'].includes(navState)} />
        )}
      </StyledScrollWrap>
      {['closed', 'closing'].includes(navState) && <StyledUtilsSeparator />}
      <Utils items={utils} />
      {agent && <AIAssistant agent={agent} />}
    </>
  );
};

export default AppNavigationPanel;
